import { useState, type ReactNode } from 'react';
import { T } from 'gt-react';
import { FlaskIcon, WarningDiamondIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { reevaluableInvoices } from '@/components/accounts-payable/bulk-actions/reevaluableInvoices';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { useCan } from '@/hooks/useCan';
import { invoiceExceptionsMenuState } from './invoiceExceptionsMenuState';
import { ReevaluateExceptionsDialog } from './ReevaluateExceptionsDialog';
import { TestExceptionRulesDialog } from './TestExceptionRulesDialog';

/**
 * Exception dry-run and live re-evaluate, as a submenu of the invoice
 * overflow menu. `children` renders that submenu inside the menu; the
 * dialogs stay mounted here so closing the menu does not unmount them.
 * Renders nothing into the menu when the invoice is still loading or the
 * user has neither permission.
 */
export function InvoiceExceptionsMenu({
  invoiceId,
  children,
}: {
  invoiceId: string;
  children: (submenu: ReactNode) => ReactNode;
}) {
  const [testOpen, setTestOpen] = useState(false);
  const [reevaluateOpen, setReevaluateOpen] = useState(false);
  const { invoice: payload } = useAPInvoice(invoiceId);
  const invoice = payload?.invoice;
  const [canTest, { isLoading: testLoading }] = useCan(
    Action.Read,
    Subject.APExceptionRuleSet,
  );
  const [canReevaluate, { isLoading: reevaluateLoading }] = useCan(
    Action.Update,
    Subject.APInvoice,
  );
  const menu = invoiceExceptionsMenuState({
    invoiceLoaded: invoice !== undefined,
    permissionsLoading: testLoading || reevaluateLoading,
    canTest,
    canReevaluate,
    isReevaluable: invoice != null && reevaluableInvoices([invoice]).length > 0,
  });

  const submenu =
    menu.show && invoice ? (
      <ExceptionsSubmenu
        showTest={menu.showTest}
        showReevaluate={menu.showReevaluate}
        onTest={() => setTestOpen(true)}
        onReevaluate={() => setReevaluateOpen(true)}
      />
    ) : null;

  return (
    <>
      {children(submenu)}
      <ExceptionsDialogs
        invoice={invoice}
        showTest={menu.showTest}
        showReevaluate={menu.showReevaluate}
        testOpen={testOpen}
        reevaluateOpen={reevaluateOpen}
        setTestOpen={setTestOpen}
        setReevaluateOpen={setReevaluateOpen}
      />
    </>
  );
}

function ExceptionsSubmenu({
  showTest,
  showReevaluate,
  onTest,
  onReevaluate,
}: {
  showTest: boolean;
  showReevaluate: boolean;
  onTest: () => void;
  onReevaluate: () => void;
}) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <WarningDiamondIcon className="mr-1.5 h-4 w-4" />
        <T context="invoice overflow menu: exceptions submenu">Exceptions</T>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {showTest && (
          <DropdownMenuItem onClick={onTest}>
            <FlaskIcon className="mr-1.5 h-4 w-4" />
            <T context="invoice slide-in menu: open the dry-run exception rules tester">
              Test Exception Rules
            </T>
          </DropdownMenuItem>
        )}
        {showReevaluate && (
          <DropdownMenuItem onClick={onReevaluate}>
            <WarningDiamondIcon className="mr-1.5 h-4 w-4" />
            <T context="invoice slide-in menu: re-evaluate exceptions">
              Re-evaluate Exceptions
            </T>
          </DropdownMenuItem>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function ExceptionsDialogs({
  invoice,
  showTest,
  showReevaluate,
  testOpen,
  reevaluateOpen,
  setTestOpen,
  setReevaluateOpen,
}: {
  invoice:
    | Parameters<typeof TestExceptionRulesDialog>[0]['invoice']
    | null
    | undefined;
  showTest: boolean;
  showReevaluate: boolean;
  testOpen: boolean;
  reevaluateOpen: boolean;
  setTestOpen: (open: boolean) => void;
  setReevaluateOpen: (open: boolean) => void;
}) {
  if (!invoice) return null;
  return (
    <>
      {showTest && (
        <TestExceptionRulesDialog
          invoice={invoice}
          open={testOpen}
          onOpenChange={setTestOpen}
        />
      )}
      {showReevaluate && (
        <ReevaluateExceptionsDialog
          invoice={invoice}
          open={reevaluateOpen}
          onOpenChange={setReevaluateOpen}
        />
      )}
    </>
  );
}
